import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { Button, IconButton } from 'seagull';
import { Badge } from 'seagull/components/Badge';
import { Box, Column, Row } from 'seagull/components/Layout';
import { ProgressBar } from 'seagull/components/ProgressBar';
import { Typography } from 'seagull/components/Typography';
import { PDF } from 'seagull/icons/content-edit';
import { CloseCross } from 'seagull/icons/essentials';
import { getTestIds } from 'seagull/utils/TestIds';
import { useIoContext } from '~/contexts';
import { useDownloadAgreementPdf } from '~/hooks/relationship/useDownloadAgreementPdf';
import { useLocaleKeys } from '~/i18n';
import { isTerminal } from '../../../taskModel';
import { useTasksDrawer } from '../../../tasksDrawerStore';
import styles from './RelationshipPdfTaskItem.module.scss';
export const GetRelationshipPdfTaskItemTID = (parentTestId)=>getTestIds(parentTestId);
function timeAgo(timestamp) {
    return formatDistanceToNow(new Date(timestamp), {
        addSuffix: true
    });
}
export const RelationshipPdfTaskItem = (param)=>{
    let { task, cancelTask, testId } = param;
    const { t: texts } = useLocaleKeys('tasksDrawer');
    const { browser: { openNewTab } } = useIoContext();
    const { retryAgreementPdf } = useDownloadAgreementPdf();
    const { setIsDrawerOpen } = useTasksDrawer();
    const { RelationshipPdfTaskItemTestIds } = useMemo(()=>GetRelationshipPdfTaskItemTID(testId), [
        testId
    ]);
    const [, refreshTimeAgo] = useState(0);
    useEffect(()=>{
        if (!isTerminal(task.status)) return;
        const id = setInterval(()=>refreshTimeAgo((n)=>n + 1), 60000);
        return ()=>clearInterval(id);
    }, [
        task.status
    ]);
    const hasUnseenChange = isTerminal(task.status) && !task.seen;
    const relationshipId = task.relationshipId;
    const onCancel = useCallback(()=>cancelTask(task.jobId), [
        task.jobId,
        cancelTask
    ]);
    const onRetry = useCallback(()=>{
        if (relationshipId == null) return;
        retryAgreementPdf(relationshipId, {
            previousJobId: task.jobId
        });
    }, [
        task.jobId,
        relationshipId,
        retryAgreementPdf
    ]);
    const onViewTasks = useCallback(()=>setIsDrawerOpen(true), [
        setIsDrawerOpen
    ]);
    const onView = useCallback(()=>{
        if (task.downloadUrl != null) openNewTab(task.downloadUrl);
    }, [
        openNewTab,
        task.downloadUrl
    ]);
    const titleBlock = /*#__PURE__*/ _jsxs(Row, {
        gap: 4,
        alignItems: "center",
        children: [
            /*#__PURE__*/ _jsx(Typography, {
                variant: "paragraph2",
                weight: "bold",
                ellipsis: true,
                testId: RelationshipPdfTaskItemTestIds.Title(),
                children: task.agreementName
            }),
            hasUnseenChange && /*#__PURE__*/ _jsx(Badge, {
                skin: "primary",
                outlined: true,
                rounded: true,
                testId: RelationshipPdfTaskItemTestIds.NewBadge(),
                children: texts.relationshipPdf.newBadge()
            })
        ]
    });
    const pageTypeIcon = /*#__PURE__*/ _jsx(Box, {
        className: styles.pageType,
        alignItems: "center",
        justifyContent: "center",
        children: /*#__PURE__*/ _jsx(PDF, {
            size: 24
        })
    });
    const cancelButton = /*#__PURE__*/ _jsx(IconButton, {
        bi: {
            name: 'task_item_cancel'
        },
        skin: "transparent",
        size: "small",
        tooltip: {
            title: texts.relationshipPdf.cancel()
        },
        testId: RelationshipPdfTaskItemTestIds.CancelButton(),
        onClick: onCancel,
        children: /*#__PURE__*/ _jsx(CloseCross, {
            size: 16
        })
    });
    if (task.status === 'failed') {
        return /*#__PURE__*/ _jsxs(Row, {
            gap: 16,
            alignItems: "center",
            testId: RelationshipPdfTaskItemTestIds.Root(),
            className: styles.row,
            children: [
                pageTypeIcon,
                /*#__PURE__*/ _jsxs(Column, {
                    gap: 2,
                    className: styles.body,
                    children: [
                        titleBlock,
                        /*#__PURE__*/ _jsx(Typography, {
                            variant: "paragraph3",
                            skin: "secondary",
                            testId: RelationshipPdfTaskItemTestIds.Subtitle(),
                            children: texts.relationshipPdf.failedSubtitle({
                                timeAgo: timeAgo(task.updatedAt)
                            })
                        })
                    ]
                }),
                relationshipId == null ? /*#__PURE__*/ _jsx(Button, {
                    skin: "primary",
                    size: "small",
                    bi: {
                        name: 'task_item_view_tasks'
                    },
                    testId: RelationshipPdfTaskItemTestIds.ViewTasksButton(),
                    onClick: onViewTasks,
                    children: texts.relationshipPdf.viewTasks()
                }) : /*#__PURE__*/ _jsx(Button, {
                    skin: "primary",
                    size: "small",
                    bi: {
                        name: 'task_item_retry'
                    },
                    testId: RelationshipPdfTaskItemTestIds.RetryButton(),
                    onClick: onRetry,
                    children: texts.relationshipPdf.retry()
                }),
                cancelButton
            ]
        });
    }
    if (task.status === 'ready') {
        return /*#__PURE__*/ _jsxs(Row, {
            gap: 16,
            alignItems: "center",
            testId: RelationshipPdfTaskItemTestIds.Root(),
            className: styles.row,
            children: [
                pageTypeIcon,
                /*#__PURE__*/ _jsxs(Column, {
                    gap: 2,
                    className: styles.body,
                    children: [
                        titleBlock,
                        /*#__PURE__*/ _jsx(Typography, {
                            variant: "paragraph3",
                            skin: "secondary",
                            testId: RelationshipPdfTaskItemTestIds.Subtitle(),
                            children: texts.relationshipPdf.readySubtitle({
                                timeAgo: timeAgo(task.updatedAt)
                            })
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx(Button, {
                    skin: "primary",
                    size: "small",
                    disabled: task.downloadUrl == null,
                    bi: {
                        name: 'task_item_view'
                    },
                    testId: RelationshipPdfTaskItemTestIds.ViewButton(),
                    onClick: onView,
                    children: texts.relationshipPdf.view()
                }),
                cancelButton
            ]
        });
    }
    return /*#__PURE__*/ _jsxs(Row, {
        gap: 16,
        alignItems: "center",
        testId: RelationshipPdfTaskItemTestIds.Root(),
        className: styles.row,
        children: [
            pageTypeIcon,
            /*#__PURE__*/ _jsxs(Column, {
                gap: 4,
                className: styles.body,
                children: [
                    titleBlock,
                    /*#__PURE__*/ _jsxs(Row, {
                        gap: 8,
                        alignItems: "center",
                        className: styles.progressRow,
                        children: [
                            /*#__PURE__*/ _jsx(ProgressBar, {
                                className: styles.progressBar,
                                completed: task.progress,
                                total: 100,
                                testId: RelationshipPdfTaskItemTestIds.ProgressBarSlot()
                            }),
                            /*#__PURE__*/ _jsx(Typography, {
                                variant: "paragraph3",
                                skin: "secondary",
                                testId: RelationshipPdfTaskItemTestIds.Progress(),
                                children: texts.common.percent({
                                    value: task.progress
                                })
                            })
                        ]
                    })
                ]
            }),
            cancelButton
        ]
    });
};
